
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ArrowLeft, Plus, Trash2, Edit2, Check, X, Bookmark, ChevronDown, ChevronRight, Layers, ListFilter, ShieldAlert, FolderPlus, Replace, FileSpreadsheet, ArrowRightLeft, Zap } from 'lucide-react';
import { User, TournamentTrick, TournamentRoundType, MasterList, RoundConfig, SelectedTrickReference } from '../types';
import { storage } from '../storage';
import * as XLSX from 'xlsx';

interface TournamentEditorProps {
  user: User;
  onUpdateUser: (updatedUser: User) => void;
  onBack: () => void;
  onQuickStart?: () => void;
}

const PRELIM_MIN_TRICK_SCORE = 1;
const PRELIM_MAX_TRICK_SCORE = 13;
const VISIBLE_POINT_MASTER_LIST_IDS = new Set(['kwc2026', 'cko2026']);
const getFinalBonusForScore = (score: number) => {
  if (score === 13) return 30;
  if (score === 14) return 100;
  if (score === 15) return 225;
  return 0;
};

const getPreferredMasterListId = (lists: MasterList[], preferredId?: string) => {
  if (preferredId && lists.some(list => list.id === preferredId)) {
    return preferredId;
  }

  return (
    lists.find(list => list.id !== 'default' && Array.isArray(list.tricks) && list.tricks.length > 0)?.id ||
    lists.find(list => list.id !== 'default')?.id ||
    lists[0]?.id ||
    'default'
  );
};

const getPreferredSourceListId = (
  lists: MasterList[],
  activeConfig?: RoundConfig,
  preferredId?: string
) => {
  if (preferredId && lists.some(list => list.id === preferredId)) {
    return preferredId;
  }

  const referencedListIds = activeConfig
    ? [
        ...activeConfig.selectedTricks.map(reference => reference.masterListId),
        ...(activeConfig.alternativeTrick ? [activeConfig.alternativeTrick.masterListId] : [])
      ]
    : [];

  const firstReferencedListId = referencedListIds.find(listId => lists.some(list => list.id === listId));
  if (firstReferencedListId) {
    return firstReferencedListId;
  }

  return getPreferredMasterListId(lists, preferredId);
};

const DRAG_HANDLE_DOTS = Array.from({ length: 6 }, (_, index) => index);
const DRAG_LONG_PRESS_MS = 280;
const DRAG_LONG_PRESS_MOVE_TOLERANCE = 8;
const DRAG_START_DISTANCE = 6;
type TouchPointLike = {
  identifier: number;
  clientX: number;
  clientY: number;
};

type TouchListLike = {
  length: number;
  [index: number]: TouchPointLike;
};

const findTouchByIdentifier = (touches: TouchListLike, identifier: number) => {
  for (let index = 0; index < touches.length; index += 1) {
      const touch = touches[index];
      if (touch && touch.identifier === identifier) {
          return touch;
      }
  }
  return null;
};

const TournamentEditor: React.FC<TournamentEditorProps> = ({ user, onUpdateUser, onBack, onQuickStart }) => {
  const [activeTab, setActiveTab] = useState<'MASTER' | 'SETUP'>('SETUP');
  
  // Master List Management State
  const [selectedMasterListId, setSelectedMasterListId] = useState<string>('default');
  const [newListName, setNewListName] = useState('');
  const [isCreatingList, setIsCreatingList] = useState(false);

  // Setup Management State
  const [roundCategory, setRoundCategory] = useState<TournamentRoundType>('PRELIM1');
  const [selectedConfigId, setSelectedConfigId] = useState<string>('');
  const [isCreatingConfig, setIsCreatingConfig] = useState(false);
  const [newConfigName, setNewConfigName] = useState('');
  const [isEditingConfigName, setIsEditingConfigName] = useState(false);
  const [editingConfigName, setEditingConfigName] = useState('');
  
  // Source View State
  const [viewingSourceListId, setViewingSourceListId] = useState<string>('default');

  // Trick Edit State
  const [newTrickName, setNewTrickName] = useState('');
  const [newTrickScore, setNewTrickScore] = useState<string>('');
  const [editingTrickId, setEditingTrickId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editScore, setEditScore] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Setup View Expanded Groups State
  const [expandedScores, setExpandedScores] = useState<Record<number, boolean>>({});
  const [previewInitialScore, setPreviewInitialScore] = useState<string>('0');
  const [pendingMoveConfirmation, setPendingMoveConfirmation] = useState<{
      idx: number;
      isAlternative: boolean;
      message: string;
  } | null>(null);
  const [isPrimaryGuideActive, setIsPrimaryGuideActive] = useState(false);
  const [isAlternativeGuideActive, setIsAlternativeGuideActive] = useState(false);
  const [draggingSelectedIndex, setDraggingSelectedIndex] = useState<number | null>(null);
  const [draggingSource, setDraggingSource] = useState<'selected' | 'alternative' | null>(null);
  const [selectedDropIndex, setSelectedDropIndex] = useState<number | null>(null);
  const [isAlternativeDropActive, setIsAlternativeDropActive] = useState(false);
  const [alternativeDropNotice, setAlternativeDropNotice] = useState<string>('');
  const [selectedLongPressReadyIndex, setSelectedLongPressReadyIndex] = useState<number | null>(null);
  const [selectedDragPreview, setSelectedDragPreview] = useState<{
      x: number;
      y: number;
      width: number;
      height: number;
      index: number;
  } | null>(null);
  const [alternativeDragPreview, setAlternativeDragPreview] = useState<{
      x: number;
      y: number;
      width: number;
  } | null>(null);
  const sourceSelectionRef = useRef<HTMLDivElement>(null);
  const selectedListRef = useRef<HTMLDivElement | null>(null);
  const selectedDropIndexRef = useRef<number | null>(null);
  const alternativeDropActiveRef = useRef(false);
  const alternativePointerDragRef = useRef({
      pointerId: -1,
      startX: 0,
      startY: 0,
      isLongPressActive: false,
      isDragging: false,
      longPressTimer: null as number | null,
      offsetX: 0,
      offsetY: 0,
      width: 0,
  });
  const alternativeTouchDragRef = useRef({
      touchId: -1,
      startX: 0,
      startY: 0,
      isLongPressActive: false,
      isDragging: false,
      longPressTimer: null as number | null,
      offsetX: 0,
      offsetY: 0,
      width: 0,
  });
  const selectedPointerDragRef = useRef({
      pointerId: -1,
      startX: 0,
      startY: 0,
      isLongPressActive: false,
      isDragging: false,
      longPressTimer: null as number | null,
      offsetX: 0,
      offsetY: 0,
      width: 0,
      height: 0,
      index: -1,
  });
  const touchScrollLockRef = useRef<{
      active: boolean;
      scrollY: number;
      bodyOverflow: string;
      bodyPosition: string;
      bodyTop: string;
      bodyWidth: string;
      htmlOverflow: string;
  }>({
      active: false,
      scrollY: 0,
      bodyOverflow: '',
      bodyPosition: '',
      bodyTop: '',
      bodyWidth: '',
      htmlOverflow: '',
  });
  const selectedTouchDragRef = useRef({
      touchId: -1,
      startX: 0,
      startY: 0,
      isLongPressActive: false,
      isDragging: false,
      longPressTimer: null as number | null,
      offsetX: 0,
      offsetY: 0,
      width: 0,
      height: 0,
      index: -1,
  });
  
  const config = user.tournamentData!;

  const getReferencedTrick = (reference: SelectedTrickReference, masterLists: MasterList[]) => {
      const list = masterLists.find(l => l.id === reference.masterListId);
      return list?.tricks.find(t => t.id === reference.trickId);
  };

  const setSelectedDropIndexSafely = (value: number | null) => {
      if (selectedDropIndexRef.current === value) {
          return;
      }
      selectedDropIndexRef.current = value;
      setSelectedDropIndex(value);
  };

  const setAlternativeDropActiveSafely = (value: boolean) => {
      if (alternativeDropActiveRef.current === value) {
          return;
      }
      alternativeDropActiveRef.current = value;
      setIsAlternativeDropActive(value);
  };

  const clearSelectedLongPressTimer = () => {
      if (selectedPointerDragRef.current.longPressTimer !== null) {
          window.clearTimeout(selectedPointerDragRef.current.longPressTimer);
          selectedPointerDragRef.current.longPressTimer = null;
      }
  };

  const clearAlternativeLongPressTimer = () => {
      if (alternativePointerDragRef.current.longPressTimer !== null) {
          window.clearTimeout(alternativePointerDragRef.current.longPressTimer);
          alternativePointerDragRef.current.longPressTimer = null;
      }
  };
  const clearSelectedTouchLongPressTimer = () => {
      if (selectedTouchDragRef.current.longPressTimer !== null) {
          window.clearTimeout(selectedTouchDragRef.current.longPressTimer);
          selectedTouchDragRef.current.longPressTimer = null;
      }
  };
  const clearAlternativeTouchLongPressTimer = () => {
      if (alternativeTouchDragRef.current.longPressTimer !== null) {
          window.clearTimeout(alternativeTouchDragRef.current.longPressTimer);
          alternativeTouchDragRef.current.longPressTimer = null;
      }
  };
  const lockTouchScroll = () => {
      if (typeof window === 'undefined' || typeof document === 'undefined') {
          return;
      }
      if (touchScrollLockRef.current.active) {
          return;
      }

      const { body, documentElement } = document;
      touchScrollLockRef.current = {
          active: true,
          scrollY: window.scrollY,
          bodyOverflow: body.style.overflow,
          bodyPosition: body.style.position,
          bodyTop: body.style.top,
          bodyWidth: body.style.width,
          htmlOverflow: documentElement.style.overflow,
      };

      body.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.top = `-${window.scrollY}px`;
      body.style.width = '100%';
      documentElement.style.overflow = 'hidden';
  };
  const unlockTouchScroll = () => {
      if (typeof window === 'undefined' || typeof document === 'undefined') {
          return;
      }
      if (!touchScrollLockRef.current.active) {
          return;
      }

      const { body, documentElement } = document;
      const { scrollY, bodyOverflow, bodyPosition, bodyTop, bodyWidth, htmlOverflow } = touchScrollLockRef.current;
      body.style.overflow = bodyOverflow;
      body.style.position = bodyPosition;
      body.style.top = bodyTop;
      body.style.width = bodyWidth;
      documentElement.style.overflow = htmlOverflow;
      window.scrollTo(0, scrollY);
      touchScrollLockRef.current.active = false;
  };

  useEffect(() => () => {
      unlockTouchScroll();
  }, []);

  const isPrelimEligibleReference = (reference: SelectedTrickReference, masterLists: MasterList[]) => {
      const trick = getReferencedTrick(reference, masterLists);
      return !!trick && trick.score >= PRELIM_MIN_TRICK_SCORE && trick.score <= PRELIM_MAX_TRICK_SCORE;
  };

  const isFinalEligibleReference = (reference: SelectedTrickReference, masterLists: MasterList[]) => {
      const trick = getReferencedTrick(reference, masterLists);
      return !!trick;
  };

  const getMinimumSelectedTrickScore = (selectedTricks: SelectedTrickReference[], masterLists: MasterList[]) => {
      const scores = selectedTricks
          .map(reference => getReferencedTrick(reference, masterLists)?.score)
          .filter((score): score is number => typeof score === 'number');
      if (scores.length === 0) return null;
      return Math.min(...scores);
  };

  const validateAlternativeAgainstSelectedTricks = (
      selectedTricks: SelectedTrickReference[],
      alternativeTrick: SelectedTrickReference | undefined,
      masterLists: MasterList[]
  ) => {
      if (!alternativeTrick) {
          return { isValid: true, minSelectedScore: getMinimumSelectedTrickScore(selectedTricks, masterLists), alternativeScore: null as number | null };
      }

      const minSelectedScore = getMinimumSelectedTrickScore(selectedTricks, masterLists);
      const alternativeScore = getReferencedTrick(alternativeTrick, masterLists)?.score ?? null;

      if (minSelectedScore === null || alternativeScore === null) {
          return { isValid: false, minSelectedScore, alternativeScore };
      }

      return {
          isValid: alternativeScore <= minSelectedScore,
          minSelectedScore,
          alternativeScore
      };
  };

  const sanitizePrelimRoundConfig = (roundConfig: RoundConfig, masterLists: MasterList[]) => {
      let changed = false;
      const selectedTricks = roundConfig.selectedTricks.filter(reference => {
          const isAllowed = isPrelimEligibleReference(reference, masterLists);
          if (!isAllowed) changed = true;
          return isAllowed;
      });

      const alternativeTrick = roundConfig.alternativeTrick && isPrelimEligibleReference(roundConfig.alternativeTrick, masterLists)
          ? roundConfig.alternativeTrick
          : undefined;

      if (roundConfig.alternativeTrick && !alternativeTrick) {
          changed = true;
      }

      const validation = validateAlternativeAgainstSelectedTricks(selectedTricks, alternativeTrick, masterLists);
      if (validation.isValid) {
          return {
              roundConfig: changed ? { ...roundConfig, selectedTricks, alternativeTrick } : roundConfig,
              changed
          };
      }

      return {
          roundConfig: { ...roundConfig, selectedTricks, alternativeTrick: undefined },
          changed: true
      };
  };

  const sanitizeTournamentConfig = (targetConfig: typeof config) => {
      let changed = false;

      const prelim1Configs = targetConfig.prelim1Configs.map(roundConfig => {
          const sanitized = sanitizePrelimRoundConfig(roundConfig, targetConfig.masterLists);
          if (sanitized.changed) changed = true;
          return sanitized.roundConfig;
      });

      const prelim2Configs = targetConfig.prelim2Configs.map(roundConfig => {
          const sanitized = sanitizePrelimRoundConfig(roundConfig, targetConfig.masterLists);
          if (sanitized.changed) changed = true;
          return sanitized.roundConfig;
      });

      const finalConfigs = targetConfig.finalConfigs.map(roundConfig => {
          const filteredSelectedTricks = roundConfig.selectedTricks.filter(reference => {
              const isAllowed = isFinalEligibleReference(reference, targetConfig.masterLists);
              if (!isAllowed) changed = true;
              return isAllowed;
          });

          if (filteredSelectedTricks.length !== roundConfig.selectedTricks.length) {
              return { ...roundConfig, selectedTricks: filteredSelectedTricks };
          }

          return roundConfig;
      });

      return {
          changed,
          config: changed ? { ...targetConfig, prelim1Configs, prelim2Configs, finalConfigs } : targetConfig
      };
  };

  const updateConfig = (newConfig: typeof config) => {
      const sanitizedResult = sanitizeTournamentConfig(newConfig);
      const updatedUser = { ...user, tournamentData: sanitizedResult.config };
      onUpdateUser(updatedUser);
      storage.save(updatedUser);
  };

  const visibleMasterLists = useMemo(() => {
    const filteredLists = config.masterLists.filter(list => VISIBLE_POINT_MASTER_LIST_IDS.has(list.id));
    return filteredLists.length > 0 ? filteredLists : config.masterLists;
  }, [config.masterLists]);

  const currentMasterList = useMemo(() => 
    visibleMasterLists.find(l => l.id === selectedMasterListId) || visibleMasterLists[0],
    [visibleMasterLists, selectedMasterListId]
  );

  const currentConfigs = useMemo(() => {
    if (roundCategory === 'PRELIM1') return config.prelim1Configs;
    if (roundCategory === 'PRELIM2') return config.prelim2Configs;
    return config.finalConfigs;
  }, [roundCategory, config]);

  const activeRoundConfig = useMemo(() => {
    return currentConfigs.find(c => c.id === selectedConfigId) || currentConfigs[0];
  }, [selectedConfigId, currentConfigs]);

  React.useEffect(() => {
      if (!selectedConfigId || !currentConfigs.find(c => c.id === selectedConfigId)) {
          setSelectedConfigId(currentConfigs[0]?.id || '');
      }
  }, [roundCategory, currentConfigs]);

  React.useEffect(() => {
      const preferredId = getPreferredMasterListId(visibleMasterLists, selectedMasterListId);
      if (preferredId !== selectedMasterListId) {
          setSelectedMasterListId(preferredId);
      }
  }, [visibleMasterLists, selectedMasterListId]);

  React.useEffect(() => {
      const preferredId = getPreferredSourceListId(visibleMasterLists, activeRoundConfig, viewingSourceListId);
      if (preferredId !== viewingSourceListId) {
          setViewingSourceListId(preferredId);
      }
  }, [visibleMasterLists, activeRoundConfig, viewingSourceListId]);

  React.useEffect(() => {
      if (activeRoundConfig?.selectedTricks.length) {
          setIsPrimaryGuideActive(false);
      }
  }, [
      activeRoundConfig?.id,
      activeRoundConfig?.selectedTricks.length
  ]);

  React.useEffect(() => {
      if (!activeRoundConfig?.alternativeTrick) {
          return;
      }
      setIsAlternativeGuideActive(false);
  }, [
      activeRoundConfig?.id,
      activeRoundConfig?.alternativeTrick?.masterListId,
      activeRoundConfig?.alternativeTrick?.trickId
  ]);

  const handleCreateRoundConfig = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!newConfigName.trim()) return;
      const newRC: RoundConfig = {
          id: Date.now().toString(),
          name: newConfigName.trim(),
          selectedTricks: []
      };
      if (roundCategory === 'PRELIM1') {
          updateConfig({ ...config, prelim1Configs: [...config.prelim1Configs, newRC] });
      } else if (roundCategory === 'PRELIM2') {
          updateConfig({ ...config, prelim2Configs: [...config.prelim2Configs, newRC] });
      } else {
          updateConfig({ ...config, finalConfigs: [...config.finalConfigs, newRC] });
      }
      setNewConfigName('');
      setIsCreatingConfig(false);
      setSelectedConfigId(newRC.id);
  };

  const handleDeleteRoundConfig = (id: string) => {
      if (currentConfigs.length <= 1) {
          alert("Must have at least one configuration. / 必须保留至少一个配置。");
          return;
      }
      if (!confirm("Delete this configuration? / 删除此配置方案？")) return;
      
      const newList = currentConfigs.filter(c => c.id !== id);
      if (roundCategory === 'PRELIM1') updateConfig({ ...config, prelim1Configs: newList });
      else if (roundCategory === 'PRELIM2') updateConfig({ ...config, prelim2Configs: newList });
      else updateConfig({ ...config, finalConfigs: newList });
      setSelectedConfigId(newList[0].id);
  };

  const handleRenameConfig = () => {
      if (!editingConfigName.trim()) return;
      const newList = currentConfigs.map(c => c.id === activeRoundConfig.id ? { ...c, name: editingConfigName.trim() } : c);
      if (roundCategory === 'PRELIM1') updateConfig({ ...config, prelim1Configs: newList });
      else if (roundCategory === 'PRELIM2') updateConfig({ ...config, prelim2Configs: newList });
      else updateConfig({ ...config, finalConfigs: newList });
      setIsEditingConfigName(false);
  };

  const handleViewingSourceListChange = (nextListId: string) => {
      if (nextListId === viewingSourceListId) return;
      setViewingSourceListId(nextListId);
  };

  const handleCreateList = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!newListName.trim()) return;
      const newList: MasterList = {
          id: Date.now().toString(),
          name: newListName.trim(),
          tricks: []
      };
      updateConfig({ ...config, masterLists: [...config.masterLists, newList] });
      setNewListName('');
      setIsCreatingList(false);
      setSelectedMasterListId(newList.id);
  };

  const handleDeleteList = (listId: string) => {
      if (listId === 'default' || listId === 'kwc2025' || listId === 'scw2025' || listId === 'cko2025' || listId === 'cko2026') return;
      if (!confirm("Delete this entire master list? / 删除整个招式表？")) return;
      const updatedLists = config.masterLists.filter(l => l.id !== listId);
      updateConfig({ ...config, masterLists: updatedLists });
      setSelectedMasterListId(getPreferredMasterListId(updatedLists));
  };

  const handleAddTrick = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (currentMasterList.isPreset) return;
      if (!newTrickName.trim() || !newTrickScore) return;
      const score = parseInt(newTrickScore);
      if (isNaN(score)) return;
      const newTrick: TournamentTrick = { id: Date.now().toString(), name: newTrickName.trim(), score };
      const updatedLists = config.masterLists.map(l => l.id === selectedMasterListId ? { ...l, tricks: [...l.tricks, newTrick] } : l);
      updateConfig({ ...config, masterLists: updatedLists });
      setNewTrickName(''); setNewTrickScore('');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || currentMasterList.isPreset) return;
      
      const reader = new FileReader();
      reader.onload = (evt) => {
          try {
              const ab = evt.target?.result;
              const wb = XLSX.read(ab, { type: 'array' });
              const wsname = wb.SheetNames[0];
              const ws = wb.Sheets[wsname];
              const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
              
              const newTricks: TournamentTrick[] = [];
              let addedCount = 0;
              
              data.forEach((row: any, index: number) => {
                  if (Array.isArray(row) && row.length >= 2) {
                      const name = String(row[0]).trim();
                      const scoreStr = String(row[1]).replace(/[^0-9.-]+/g, '');
                      const score = parseInt(scoreStr);
                      
                      // Skip common headers and invalid data
                      if (name && !['Name', 'name', '姓名', '招式', 'Trick', 'trick'].includes(name) && !isNaN(score)) {
                          newTricks.push({
                              id: Date.now().toString() + '_' + index,
                              name: name,
                              score: score
                          });
                          addedCount++;
                      }
                  }
              });

              if (newTricks.length === 0) {
                  alert('No valid tricks found. Please ensure the Excel file has two columns: [Trick Name] and [Score]. / 未找到有效的招式。请确保Excel表格包含两列：[招式名称] 和 [分数]。');
                  return;
              }

              const updatedLists = config.masterLists.map(l => 
                  l.id === selectedMasterListId 
                  ? { ...l, tricks: [...l.tricks, ...newTricks] } 
                  : l
              );
              updateConfig({ ...config, masterLists: updatedLists });
              
              alert(`Successfully imported ${addedCount} tricks! / 成功导入 ${addedCount} 个招式！`);
              
              if (fileInputRef.current) fileInputRef.current.value = '';
          } catch (err: any) {
              alert('Error parsing Excel file / 解析Excel出错: ' + err.message);
          }
      };
      reader.readAsArrayBuffer(file);
  };

  const handleDeleteTrick = (trickId: string) => {
      if (currentMasterList.isPreset) return;
      if (!confirm("Delete this trick? / 删除此招式？")) return;
      const updatedLists = config.masterLists.map(l => 
          l.id === selectedMasterListId 
          ? { ...l, tricks: l.tricks.filter(t => t.id !== trickId) } 
          : l
      );
      updateConfig({ ...config, masterLists: updatedLists });
  };

  const saveEditing = () => {
      if (!editingTrickId || !editName.trim() || !editScore) return;
      const score = parseInt(editScore);
      if (isNaN(score)) return;
      const updatedLists = config.masterLists.map(l => l.id === selectedMasterListId ? { ...l, tricks: l.tricks.map(t => t.id === editingTrickId ? { ...t, name: editName.trim(), score } : t) } : l);
      updateConfig({ ...config, masterLists: updatedLists });
      setEditingTrickId(null);
  };

  const groupedTricks = useMemo(() => {
    const groups: Record<number, TournamentTrick[]> = {};
    currentMasterList.tricks.forEach(t => {
      if (!groups[t.score]) groups[t.score] = [];
      groups[t.score].push(t);
    });
    return Object.keys(groups).map(Number).sort((a, b) => a - b).map(score => ({ score, tricks: groups[score] }));
  }, [currentMasterList]);

  const handleAddRoundTrick = (trickId: string, isAlternative: boolean = false) => {
      if (isAlternative) {
          const newAlt: SelectedTrickReference = { trickId, masterListId: viewingSourceListId };
          const validation = validateAlternativeAgainstSelectedTricks(activeRoundConfig.selectedTricks, newAlt, config.masterLists);
          if (validation.minSelectedScore === null) {
              alert('Please select regular tricks first before setting an alternative. / 请先选择正式招式，再设置备选招式。');
              return;
          }
          if (!validation.isValid) {
              alert(`Alternative trick must be equal to or lower than the lowest regular trick score (${validation.minSelectedScore} pts). / 备选招式只能选择小于等于正式招式最低分（${validation.minSelectedScore}分）的招式。`);
              return;
          }
          const newList = currentConfigs.map(c => c.id === activeRoundConfig.id ? { ...c, alternativeTrick: newAlt } : c);
          setIsPrimaryGuideActive(false);
          setIsAlternativeGuideActive(false);
          if (roundCategory === 'PRELIM1') updateConfig({ ...config, prelim1Configs: newList });
          else if (roundCategory === 'PRELIM2') updateConfig({ ...config, prelim2Configs: newList });
          return;
      }

      const limit = roundCategory === 'FINAL' ? 30 : 10;
      if (activeRoundConfig.selectedTricks.length >= limit) { alert(`Round full (Max ${limit}).`); return; }
      if (activeRoundConfig.selectedTricks.some(t => t.trickId === trickId && t.masterListId === viewingSourceListId)) return;

      const selectedTrick = currentViewingSourceList.tricks.find(t => t.id === trickId);
      if (!selectedTrick) {
          return;
      }
      if (roundCategory !== 'FINAL' && (selectedTrick.score < PRELIM_MIN_TRICK_SCORE || selectedTrick.score > PRELIM_MAX_TRICK_SCORE)) {
          alert(`Prelims only allow ${PRELIM_MIN_TRICK_SCORE}-${PRELIM_MAX_TRICK_SCORE} point tricks. / 初赛只能选择 ${PRELIM_MIN_TRICK_SCORE}-${PRELIM_MAX_TRICK_SCORE} 分的招式。`);
          return;
      }
      
      const newSelection: SelectedTrickReference = { trickId, masterListId: viewingSourceListId };
      let removedInvalidAlternative = false;
      const newList = currentConfigs.map(c => {
          if (c.id !== activeRoundConfig.id) return c;
          const nextConfig = { ...c, selectedTricks: [...c.selectedTricks, newSelection] };
          const sanitized = sanitizePrelimRoundConfig(nextConfig, config.masterLists);
            if (sanitized.changed) removedInvalidAlternative = true;
          return sanitized.roundConfig;
      });
      
      setIsPrimaryGuideActive(false);
      if (roundCategory === 'PRELIM1') updateConfig({ ...config, prelim1Configs: newList });
      else if (roundCategory === 'PRELIM2') updateConfig({ ...config, prelim2Configs: newList });
      else updateConfig({ ...config, finalConfigs: newList });
      if (removedInvalidAlternative) {
          alert('The alternative trick was cleared because it became higher than the lowest regular trick score. / 备选招式已自动清除，因为它高于当前正式招式中的最低分。');
      }
  };

  const handleRemoveRoundTrick = (idx: number, isAlternative: boolean = false) => {
      if (isAlternative) {
          const newList = currentConfigs.map(c => c.id === activeRoundConfig.id ? { ...c, alternativeTrick: undefined } : c);
          if (roundCategory === 'PRELIM1') updateConfig({ ...config, prelim1Configs: newList });
          else if (roundCategory === 'PRELIM2') updateConfig({ ...config, prelim2Configs: newList });
          return;
      }
      const newList = currentConfigs.map(c => {
          if (c.id !== activeRoundConfig.id) return c;
          const nextConfig = { ...c, selectedTricks: c.selectedTricks.filter((_, i) => i !== idx) };
          return sanitizePrelimRoundConfig(nextConfig, config.masterLists).roundConfig;
      });
      if (roundCategory === 'PRELIM1') updateConfig({ ...config, prelim1Configs: newList });
      else if (roundCategory === 'PRELIM2') updateConfig({ ...config, prelim2Configs: newList });
      else updateConfig({ ...config, finalConfigs: newList });
  };

  const requestMoveTrickToOtherPrelim = (idx: number, isAlternative: boolean = false) => {
      const isToPrelim2 = roundCategory === 'PRELIM1';
      const trickToMove = isAlternative
          ? activeRoundConfig.alternativeTrick
          : activeRoundConfig.selectedTricks[idx];

      if (!trickToMove) return;

      const sourceList = config.masterLists.find(list => list.id === trickToMove.masterListId);
      const sourceTrick = sourceList?.tricks.find(trick => trick.id === trickToMove.trickId);
      const targetRoundLabel = isToPrelim2 ? '初赛 2' : '初赛 1';
      const message = isAlternative
          ? `是否将备选招式“${sourceTrick?.name || 'Unknown'}”移动到另一组${targetRoundLabel}？`
          : `是否将此招式“${sourceTrick?.name || 'Unknown'}”移动到另一组${targetRoundLabel}？`;

      setPendingMoveConfirmation({ idx, isAlternative, message });
  };

  const handleMoveTrickToOtherPrelim = (idx: number, isAlternative: boolean = false) => {
      const isToPrelim2 = roundCategory === 'PRELIM1';
      const targetConfigs = isToPrelim2 ? config.prelim2Configs : config.prelim1Configs;
      
      if (targetConfigs.length === 0) {
          alert("Target configuration not found. / 找不到目标配置。");
          return;
      }
      
      const targetConfig = targetConfigs[0];
      
      let trickToMove: SelectedTrickReference | undefined;
      if (isAlternative) {
          trickToMove = activeRoundConfig.alternativeTrick;
      } else {
          trickToMove = activeRoundConfig.selectedTricks[idx];
      }
      
      if (!trickToMove) return;

      if (!isAlternative && targetConfig.selectedTricks.length >= 10) {
          alert("Target round is full (Max 10). / 目标轮次已满 (最多10招)。");
          return;
      }

      if (isAlternative) {
          const targetValidation = validateAlternativeAgainstSelectedTricks(targetConfig.selectedTricks, trickToMove, config.masterLists);
          if (targetValidation.minSelectedScore === null) {
              alert('Target round needs at least one regular trick before setting an alternative. / 目标轮次需要先有正式招式，才能设置备选招式。');
              return;
          }
          if (!targetValidation.isValid) {
              alert(`Alternative trick must be equal to or lower than the target round's lowest regular trick score (${targetValidation.minSelectedScore} pts). / 备选招式只能选择小于等于目标轮次正式招式最低分（${targetValidation.minSelectedScore}分）的招式。`);
              return;
          }
      }

      // Remove from current
      const newList = currentConfigs.map(c => {
          if (c.id === activeRoundConfig.id) {
              if (isAlternative) {
                  return { ...c, alternativeTrick: undefined };
              } else {
                  const nextConfig = { ...c, selectedTricks: c.selectedTricks.filter((_, i) => i !== idx) };
                  return sanitizePrelimRoundConfig(nextConfig, config.masterLists).roundConfig;
              }
          }
          return c;
      });

      // Add to target
      let removedInvalidAlternative = false;
      const newTargetList = targetConfigs.map(c => {
          if (c.id === targetConfig.id) {
              if (isAlternative) {
                  return { ...c, alternativeTrick: trickToMove };
              } else {
                  const nextConfig = { ...c, selectedTricks: [...c.selectedTricks, trickToMove] };
                  const sanitized = sanitizePrelimRoundConfig(nextConfig, config.masterLists);
                    if (sanitized.changed) removedInvalidAlternative = true;
                  return sanitized.roundConfig;
              }
          }
          return c;
      });

      const newConfig = { ...config };
      if (isToPrelim2) {
          newConfig.prelim1Configs = newList;
          newConfig.prelim2Configs = newTargetList;
      } else {
          newConfig.prelim2Configs = newList;
          newConfig.prelim1Configs = newTargetList;
      }
      
      updateConfig(newConfig);
      if (removedInvalidAlternative) {
          alert('The target round alternative trick was cleared because it became higher than the lowest regular trick score. / 目标轮次的备选招式已自动清除，因为它高于当前正式招式中的最低分。');
      }
  };

  const commitSelectedTrickReorder = (draggedIndex: number, rawDropIndex: number) => {
      if (
          draggedIndex < 0 ||
          rawDropIndex < 0 ||
          draggedIndex >= activeRoundConfig.selectedTricks.length ||
          rawDropIndex > activeRoundConfig.selectedTricks.length
      ) {
          return;
      }

      const targetIndex = draggedIndex < rawDropIndex ? rawDropIndex - 1 : rawDropIndex;
      if (draggedIndex === targetIndex) {
          return;
      }

      const newSelectedTricks = [...activeRoundConfig.selectedTricks];
      const [draggedItem] = newSelectedTricks.splice(draggedIndex, 1);
      newSelectedTricks.splice(targetIndex, 0, draggedItem);

      const newList = currentConfigs.map(c => c.id === activeRoundConfig.id ? { ...c, selectedTricks: newSelectedTricks } : c);
      if (roundCategory === 'PRELIM1') updateConfig({ ...config, prelim1Configs: newList });
      else if (roundCategory === 'PRELIM2') updateConfig({ ...config, prelim2Configs: newList });
      else updateConfig({ ...config, finalConfigs: newList });
  };

  const moveSelectedTrickToAlternative = (sourceIndex: number) => {
      const sourceSelection = activeRoundConfig.selectedTricks[sourceIndex];
      if (!sourceSelection) {
          return;
      }

      const nextSelectedTricks = activeRoundConfig.selectedTricks.filter((_, index) => index !== sourceIndex);
      const existingAlternative = activeRoundConfig.alternativeTrick;

      if (!existingAlternative && nextSelectedTricks.length === 0) {
          setAlternativeDropNotice('请至少保留一个正式招式后再设置备选招式。');
          return;
      }

      const validation = validateAlternativeAgainstSelectedTricks(nextSelectedTricks, sourceSelection, config.masterLists);
      if (validation.minSelectedScore === null) {
          setAlternativeDropNotice('请先选择正式招式，再设置备选招式。');
          return;
      }
      if (!validation.isValid) {
          setAlternativeDropNotice(`备选招式只能选择小于等于正式招式最低分（${validation.minSelectedScore}分）的招式。`);
          return;
      }

      const newList = currentConfigs.map(c => {
          if (c.id !== activeRoundConfig.id) return c;

          const reorderedSelectedTricks = existingAlternative
              ? [
                  ...nextSelectedTricks.slice(0, sourceIndex),
                  existingAlternative,
                  ...nextSelectedTricks.slice(sourceIndex),
                ]
              : nextSelectedTricks;

          return {
              ...c,
              selectedTricks: reorderedSelectedTricks,
              alternativeTrick: sourceSelection,
          };
      });

      setIsAlternativeGuideActive(false);
      setAlternativeDropNotice('');
      if (roundCategory === 'PRELIM1') updateConfig({ ...config, prelim1Configs: newList });
      else if (roundCategory === 'PRELIM2') updateConfig({ ...config, prelim2Configs: newList });
  };

  const moveAlternativeToSelected = (rawDropIndex: number) => {
      const alternativeSelection = activeRoundConfig.alternativeTrick;
      if (!alternativeSelection) {
          return;
      }

      const limit = roundCategory === 'FINAL' ? 30 : 10;
      if (activeRoundConfig.selectedTricks.length >= limit) {
          setAlternativeDropNotice(`正式招式列表已满（最多 ${limit} 个），无法拖回。`);
          return;
      }

      const targetIndex = Math.min(Math.max(0, rawDropIndex), activeRoundConfig.selectedTricks.length);
      const nextSelectedTricks = [...activeRoundConfig.selectedTricks];
      nextSelectedTricks.splice(targetIndex, 0, alternativeSelection);

      const newList = currentConfigs.map(c => c.id === activeRoundConfig.id
          ? { ...c, selectedTricks: nextSelectedTricks, alternativeTrick: undefined }
          : c
      );

      setAlternativeDropNotice('');
      if (roundCategory === 'PRELIM1') updateConfig({ ...config, prelim1Configs: newList });
      else if (roundCategory === 'PRELIM2') updateConfig({ ...config, prelim2Configs: newList });
  };

  const getDragPayload = (event: React.DragEvent<HTMLDivElement>) => {
      const source = event.dataTransfer.getData('application/x-kendama-drag-source');
      const draggedIndexStr = event.dataTransfer.getData('text/plain');
      if (source === 'alternative') {
          return { source: 'alternative' as const, index: null };
      }

      if (!draggedIndexStr) return null;
      const draggedIndex = parseInt(draggedIndexStr, 10);
      if (Number.isNaN(draggedIndex)) return null;
      return { source: 'selected' as const, index: draggedIndex };
  };

  const getDropIndexFromEvent = (event: React.DragEvent<HTMLDivElement>, index: number) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const midpointY = rect.top + rect.height / 2;
      return event.clientY < midpointY ? index : index + 1;
  };

  const updateAlternativePointerDropTarget = (clientX: number, clientY: number) => {
      const hoveredElement = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
      const selectedIndexElement = hoveredElement?.closest('[data-selected-index]') as HTMLElement | null;
      if (selectedIndexElement) {
          const index = Number(selectedIndexElement.dataset.selectedIndex);
          if (Number.isNaN(index)) {
              setSelectedDropIndexSafely(null);
              return;
          }
          const rect = selectedIndexElement.getBoundingClientRect();
          const midpointY = rect.top + rect.height / 2;
          setSelectedDropIndexSafely(clientY < midpointY ? index : index + 1);
          return;
      }

      const selectedList = selectedListRef.current;
      if (selectedList && hoveredElement && selectedList.contains(hoveredElement)) {
          setSelectedDropIndexSafely(activeRoundConfig.selectedTricks.length);
          return;
      }

      setSelectedDropIndexSafely(null);
  };

  const updateSelectedPointerDropTarget = (clientX: number, clientY: number, draggedIndex: number) => {
      const hoveredElement = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
      const alternativeDropZone = hoveredElement?.closest('[data-alternative-drop-zone]') as HTMLElement | null;
      if (alternativeDropZone) {
          setAlternativeDropActiveSafely(true);
          setSelectedDropIndexSafely(null);
          return;
      }

      setAlternativeDropActiveSafely(false);

      const selectedIndexElement = hoveredElement?.closest('[data-selected-index]') as HTMLElement | null;
      if (selectedIndexElement) {
          const index = Number(selectedIndexElement.dataset.selectedIndex);
          if (Number.isNaN(index)) {
              setSelectedDropIndexSafely(null);
              return;
          }
          const rect = selectedIndexElement.getBoundingClientRect();
          const midpointY = rect.top + rect.height / 2;
          const nextIndex = clientY < midpointY ? index : index + 1;
          setSelectedDropIndexSafely(nextIndex);
          return;
      }

      const selectedList = selectedListRef.current;
      if (selectedList && hoveredElement && selectedList.contains(hoveredElement)) {
          setSelectedDropIndexSafely(activeRoundConfig.selectedTricks.length);
          return;
      }

      setSelectedDropIndexSafely(draggedIndex);
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
      e.dataTransfer.setData('application/x-kendama-drag-source', 'selected');
      e.dataTransfer.setData('text/plain', index.toString());
      e.dataTransfer.effectAllowed = 'move';
      setDraggingSource('selected');
      setDraggingSelectedIndex(index);
      setSelectedDropIndexSafely(index);
      setAlternativeDropActiveSafely(false);
      setAlternativeDropNotice('');
  };

  const handleSelectedPointerDown = (e: React.PointerEvent<HTMLDivElement>, index: number) => {
      if (e.pointerType === 'touch') {
          return;
      }
      if (e.button !== 0) {
          return;
      }
      if ((e.target as HTMLElement).closest('button')) {
          return;
      }

      const rect = e.currentTarget.getBoundingClientRect();
      const pointerId = e.pointerId;
      setSelectedLongPressReadyIndex(null);
      selectedPointerDragRef.current = {
          pointerId,
          startX: e.clientX,
          startY: e.clientY,
          isLongPressActive: false,
          isDragging: false,
          longPressTimer: window.setTimeout(() => {
              if (selectedPointerDragRef.current.pointerId === pointerId) {
                  selectedPointerDragRef.current.isLongPressActive = true;
                  selectedPointerDragRef.current.longPressTimer = null;
                  setSelectedLongPressReadyIndex(index);
              }
          }, DRAG_LONG_PRESS_MS),
          offsetX: e.clientX - rect.left,
          offsetY: e.clientY - rect.top,
          width: rect.width,
          height: rect.height,
          index,
      };
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.setPointerCapture(e.pointerId);
      }
      setAlternativeDropNotice('');
  };

  const handleSelectedPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'touch') {
          return;
      }
      const dragState = selectedPointerDragRef.current;
      if (dragState.pointerId !== e.pointerId) {
          return;
      }

      const deltaX = e.clientX - dragState.startX;
      const deltaY = e.clientY - dragState.startY;
      if (!dragState.isDragging) {
          const moveDistance = Math.hypot(deltaX, deltaY);
          if (!dragState.isLongPressActive) {
              if (moveDistance > DRAG_LONG_PRESS_MOVE_TOLERANCE) {
                  clearSelectedLongPressTimer();
                  setSelectedLongPressReadyIndex(null);
              }
              return;
          }
          e.preventDefault();
          clearSelectedLongPressTimer();
          if (moveDistance < DRAG_START_DISTANCE) {
              return;
          }
          setSelectedLongPressReadyIndex(null);
          dragState.isDragging = true;
          setDraggingSource('selected');
          setDraggingSelectedIndex(dragState.index);
      }

      setSelectedDragPreview({
          x: e.clientX - dragState.offsetX,
          y: e.clientY - dragState.offsetY,
          width: dragState.width,
          height: dragState.height,
          index: dragState.index,
      });
      updateSelectedPointerDropTarget(e.clientX, e.clientY, dragState.index);
  };

  const finishSelectedPointerDrag = (pointerId: number, clientX: number, clientY: number) => {
      const dragState = selectedPointerDragRef.current;
      if (dragState.pointerId !== pointerId) {
          return;
      }
      clearSelectedLongPressTimer();
      setSelectedLongPressReadyIndex(null);

      if (dragState.isDragging) {
          updateSelectedPointerDropTarget(clientX, clientY, dragState.index);
          if (alternativeDropActiveRef.current) {
              moveSelectedTrickToAlternative(dragState.index);
          } else {
              const nextDropIndex = selectedDropIndexRef.current;
              if (nextDropIndex !== null) {
                  commitSelectedTrickReorder(dragState.index, nextDropIndex);
              }
          }
      }

      selectedPointerDragRef.current = {
          pointerId: -1,
          startX: 0,
          startY: 0,
          isLongPressActive: false,
          isDragging: false,
          longPressTimer: null,
          offsetX: 0,
          offsetY: 0,
          width: 0,
          height: 0,
          index: -1,
      };
      setSelectedDragPreview(null);
      setDraggingSource(null);
      setDraggingSelectedIndex(null);
      setSelectedDropIndexSafely(null);
      setAlternativeDropActiveSafely(false);
  };

  const handleSelectedPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'touch') {
          return;
      }
      if (selectedPointerDragRef.current.pointerId === e.pointerId && e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.releasePointerCapture(e.pointerId);
      }
      finishSelectedPointerDrag(e.pointerId, e.clientX, e.clientY);
  };

  const handleSelectedPointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'touch') {
          return;
      }
      if (selectedPointerDragRef.current.pointerId === e.pointerId && e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.releasePointerCapture(e.pointerId);
      }
      finishSelectedPointerDrag(e.pointerId, e.clientX, e.clientY);
  };
  const handleSelectedTouchStart = (e: React.TouchEvent<HTMLDivElement>, index: number) => {
      if ((e.target as HTMLElement).closest('button')) {
          return;
      }
      const touch = e.changedTouches[0];
      if (!touch) {
          return;
      }
      const rect = e.currentTarget.getBoundingClientRect();
      const touchId = touch.identifier;
      setSelectedLongPressReadyIndex(null);
      selectedTouchDragRef.current = {
          touchId,
          startX: touch.clientX,
          startY: touch.clientY,
          isLongPressActive: false,
          isDragging: false,
          longPressTimer: window.setTimeout(() => {
              if (selectedTouchDragRef.current.touchId === touchId) {
                  selectedTouchDragRef.current.isLongPressActive = true;
                  selectedTouchDragRef.current.longPressTimer = null;
                  setSelectedLongPressReadyIndex(index);
              }
          }, DRAG_LONG_PRESS_MS),
          offsetX: touch.clientX - rect.left,
          offsetY: touch.clientY - rect.top,
          width: rect.width,
          height: rect.height,
          index,
      };
      setAlternativeDropNotice('');
  };
  const handleSelectedTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
      const dragState = selectedTouchDragRef.current;
      const touch = findTouchByIdentifier(e.changedTouches, dragState.touchId)
          ?? findTouchByIdentifier(e.touches, dragState.touchId);
      if (!touch) {
          return;
      }

      const deltaX = touch.clientX - dragState.startX;
      const deltaY = touch.clientY - dragState.startY;
      if (!dragState.isDragging) {
          const moveDistance = Math.hypot(deltaX, deltaY);
          if (!dragState.isLongPressActive) {
              if (moveDistance > DRAG_LONG_PRESS_MOVE_TOLERANCE) {
                  clearSelectedTouchLongPressTimer();
                  setSelectedLongPressReadyIndex(null);
              }
              return;
          }
          e.preventDefault();
          clearSelectedTouchLongPressTimer();
          if (moveDistance < DRAG_START_DISTANCE) {
              return;
          }
          lockTouchScroll();
          setSelectedLongPressReadyIndex(null);
          dragState.isDragging = true;
          setDraggingSource('selected');
          setDraggingSelectedIndex(dragState.index);
      } else {
          e.preventDefault();
      }

      setSelectedDragPreview({
          x: touch.clientX - dragState.offsetX,
          y: touch.clientY - dragState.offsetY,
          width: dragState.width,
          height: dragState.height,
          index: dragState.index,
      });
      updateSelectedPointerDropTarget(touch.clientX, touch.clientY, dragState.index);
  };
  const finishSelectedTouchDrag = (touchId: number, clientX: number, clientY: number) => {
      const dragState = selectedTouchDragRef.current;
      if (dragState.touchId !== touchId) {
          return;
      }
      clearSelectedTouchLongPressTimer();
      setSelectedLongPressReadyIndex(null);
      unlockTouchScroll();

      if (dragState.isDragging) {
          updateSelectedPointerDropTarget(clientX, clientY, dragState.index);
          if (alternativeDropActiveRef.current) {
              moveSelectedTrickToAlternative(dragState.index);
          } else {
              const nextDropIndex = selectedDropIndexRef.current;
              if (nextDropIndex !== null) {
                  commitSelectedTrickReorder(dragState.index, nextDropIndex);
              }
          }
      }

      selectedTouchDragRef.current = {
          touchId: -1,
          startX: 0,
          startY: 0,
          isLongPressActive: false,
          isDragging: false,
          longPressTimer: null,
          offsetX: 0,
          offsetY: 0,
          width: 0,
          height: 0,
          index: -1,
      };
      setSelectedDragPreview(null);
      setDraggingSource(null);
      setDraggingSelectedIndex(null);
      setSelectedDropIndexSafely(null);
      setAlternativeDropActiveSafely(false);
  };
  const handleSelectedTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
      const touch = findTouchByIdentifier(e.changedTouches, selectedTouchDragRef.current.touchId);
      if (!touch) {
          return;
      }
      finishSelectedTouchDrag(touch.identifier, touch.clientX, touch.clientY);
  };
  const handleSelectedTouchCancel = (e: React.TouchEvent<HTMLDivElement>) => {
      const touch = findTouchByIdentifier(e.changedTouches, selectedTouchDragRef.current.touchId);
      if (!touch) {
          finishSelectedTouchDrag(selectedTouchDragRef.current.touchId, selectedTouchDragRef.current.startX, selectedTouchDragRef.current.startY);
          return;
      }
      finishSelectedTouchDrag(touch.identifier, touch.clientX, touch.clientY);
  };

  const handleAlternativePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'touch') {
          return;
      }
      if (e.button !== 0) {
          return;
      }
      if ((e.target as HTMLElement).closest('button')) {
          return;
      }

      const pointerId = e.pointerId;
      alternativePointerDragRef.current = {
          pointerId,
          startX: e.clientX,
          startY: e.clientY,
          isLongPressActive: false,
          isDragging: false,
          longPressTimer: window.setTimeout(() => {
              if (alternativePointerDragRef.current.pointerId === pointerId) {
                  alternativePointerDragRef.current.isLongPressActive = true;
                  alternativePointerDragRef.current.longPressTimer = null;
              }
          }, DRAG_LONG_PRESS_MS),
          offsetX: e.clientX - e.currentTarget.getBoundingClientRect().left,
          offsetY: e.clientY - e.currentTarget.getBoundingClientRect().top,
          width: e.currentTarget.getBoundingClientRect().width,
      };
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.setPointerCapture(e.pointerId);
      }
      setAlternativeDropNotice('');
  };

  const handleAlternativePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'touch') {
          return;
      }
      const dragState = alternativePointerDragRef.current;
      if (dragState.pointerId !== e.pointerId) {
          return;
      }

      const deltaX = e.clientX - dragState.startX;
      const deltaY = e.clientY - dragState.startY;
      if (!dragState.isDragging) {
          const moveDistance = Math.hypot(deltaX, deltaY);
          if (!dragState.isLongPressActive) {
              if (moveDistance > DRAG_LONG_PRESS_MOVE_TOLERANCE) {
                  clearAlternativeLongPressTimer();
              }
              return;
          }
          e.preventDefault();
          clearAlternativeLongPressTimer();
          if (moveDistance < DRAG_START_DISTANCE) {
              return;
          }
          dragState.isDragging = true;
          setDraggingSource('alternative');
          setDraggingSelectedIndex(null);
      }

      setAlternativeDragPreview({
          x: e.clientX - dragState.offsetX,
          y: e.clientY - dragState.offsetY,
          width: dragState.width,
      });
      updateAlternativePointerDropTarget(e.clientX, e.clientY);
  };

  const finishAlternativePointerDrag = (pointerId: number, clientX: number, clientY: number) => {
      const dragState = alternativePointerDragRef.current;
      if (dragState.pointerId !== pointerId) {
          return;
      }
      clearAlternativeLongPressTimer();

      if (dragState.isDragging) {
          updateAlternativePointerDropTarget(clientX, clientY);
          const nextDropIndex = selectedDropIndexRef.current;
          if (nextDropIndex !== null) {
              moveAlternativeToSelected(nextDropIndex);
          }
      }

      alternativePointerDragRef.current = {
          pointerId: -1,
          startX: 0,
          startY: 0,
          isLongPressActive: false,
          isDragging: false,
          longPressTimer: null,
          offsetX: 0,
          offsetY: 0,
          width: 0,
      };
      setAlternativeDragPreview(null);
      setDraggingSource(null);
      setDraggingSelectedIndex(null);
      setSelectedDropIndexSafely(null);
      setAlternativeDropActiveSafely(false);
  };

  const handleAlternativePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'touch') {
          return;
      }
      if (alternativePointerDragRef.current.pointerId === e.pointerId && e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.releasePointerCapture(e.pointerId);
      }
      finishAlternativePointerDrag(e.pointerId, e.clientX, e.clientY);
  };

  const handleAlternativePointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'touch') {
          return;
      }
      if (alternativePointerDragRef.current.pointerId === e.pointerId && e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.releasePointerCapture(e.pointerId);
      }
      finishAlternativePointerDrag(e.pointerId, e.clientX, e.clientY);
  };
  const handleAlternativeTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest('button')) {
          return;
      }
      const touch = e.changedTouches[0];
      if (!touch) {
          return;
      }
      const rect = e.currentTarget.getBoundingClientRect();
      const touchId = touch.identifier;
      alternativeTouchDragRef.current = {
          touchId,
          startX: touch.clientX,
          startY: touch.clientY,
          isLongPressActive: false,
          isDragging: false,
          longPressTimer: window.setTimeout(() => {
              if (alternativeTouchDragRef.current.touchId === touchId) {
                  alternativeTouchDragRef.current.isLongPressActive = true;
                  alternativeTouchDragRef.current.longPressTimer = null;
              }
          }, DRAG_LONG_PRESS_MS),
          offsetX: touch.clientX - rect.left,
          offsetY: touch.clientY - rect.top,
          width: rect.width,
      };
      setAlternativeDropNotice('');
  };
  const handleAlternativeTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
      const dragState = alternativeTouchDragRef.current;
      const touch = findTouchByIdentifier(e.changedTouches, dragState.touchId)
          ?? findTouchByIdentifier(e.touches, dragState.touchId);
      if (!touch) {
          return;
      }

      const deltaX = touch.clientX - dragState.startX;
      const deltaY = touch.clientY - dragState.startY;
      if (!dragState.isDragging) {
          const moveDistance = Math.hypot(deltaX, deltaY);
          if (!dragState.isLongPressActive) {
              if (moveDistance > DRAG_LONG_PRESS_MOVE_TOLERANCE) {
                  clearAlternativeTouchLongPressTimer();
              }
              return;
          }
          e.preventDefault();
          clearAlternativeTouchLongPressTimer();
          if (moveDistance < DRAG_START_DISTANCE) {
              return;
          }
          lockTouchScroll();
          dragState.isDragging = true;
          setDraggingSource('alternative');
          setDraggingSelectedIndex(null);
      } else {
          e.preventDefault();
      }

      setAlternativeDragPreview({
          x: touch.clientX - dragState.offsetX,
          y: touch.clientY - dragState.offsetY,
          width: dragState.width,
      });
      updateAlternativePointerDropTarget(touch.clientX, touch.clientY);
  };
  const finishAlternativeTouchDrag = (touchId: number, clientX: number, clientY: number) => {
      const dragState = alternativeTouchDragRef.current;
      if (dragState.touchId !== touchId) {
          return;
      }
      clearAlternativeTouchLongPressTimer();
      unlockTouchScroll();

      if (dragState.isDragging) {
          updateAlternativePointerDropTarget(clientX, clientY);
          const nextDropIndex = selectedDropIndexRef.current;
          if (nextDropIndex !== null) {
              moveAlternativeToSelected(nextDropIndex);
          }
      }

      alternativeTouchDragRef.current = {
          touchId: -1,
          startX: 0,
          startY: 0,
          isLongPressActive: false,
          isDragging: false,
          longPressTimer: null,
          offsetX: 0,
          offsetY: 0,
          width: 0,
      };
      setAlternativeDragPreview(null);
      setDraggingSource(null);
      setDraggingSelectedIndex(null);
      setSelectedDropIndexSafely(null);
      setAlternativeDropActiveSafely(false);
  };
  const handleAlternativeTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
      const touch = findTouchByIdentifier(e.changedTouches, alternativeTouchDragRef.current.touchId);
      if (!touch) {
          return;
      }
      finishAlternativeTouchDrag(touch.identifier, touch.clientX, touch.clientY);
  };
  const handleAlternativeTouchCancel = (e: React.TouchEvent<HTMLDivElement>) => {
      const touch = findTouchByIdentifier(e.changedTouches, alternativeTouchDragRef.current.touchId);
      if (!touch) {
          finishAlternativeTouchDrag(alternativeTouchDragRef.current.touchId, alternativeTouchDragRef.current.startX, alternativeTouchDragRef.current.startY);
          return;
      }
      finishAlternativeTouchDrag(touch.identifier, touch.clientX, touch.clientY);
  };

  const handleDragEnd = () => {
      setSelectedDragPreview(null);
      setAlternativeDragPreview(null);
      setDraggingSource(null);
      setDraggingSelectedIndex(null);
      setSelectedDropIndexSafely(null);
      setAlternativeDropActiveSafely(false);
  };

  const handleItemDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setSelectedDropIndexSafely(getDropIndexFromEvent(e, index));
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, fallbackIndex: number) => {
      e.preventDefault();
      const dragPayload = getDragPayload(e);
      if (!dragPayload) return;
      const nextDropIndex = selectedDropIndex ?? fallbackIndex;

      if (dragPayload.source === 'alternative') {
          moveAlternativeToSelected(nextDropIndex);
      } else {
          commitSelectedTrickReorder(dragPayload.index, nextDropIndex);
      }
      setDraggingSource(null);
      setDraggingSelectedIndex(null);
      setSelectedDropIndexSafely(null);
      setAlternativeDropActiveSafely(false);
  };

  const handleListDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (activeRoundConfig.selectedTricks.length === 0) {
          setSelectedDropIndexSafely(0);
      }
  };

  const handleListDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const dragPayload = getDragPayload(e);
      if (!dragPayload) return;
      const nextDropIndex = selectedDropIndex ?? activeRoundConfig.selectedTricks.length;

      if (dragPayload.source === 'alternative') {
          moveAlternativeToSelected(nextDropIndex);
      } else {
          commitSelectedTrickReorder(dragPayload.index, nextDropIndex);
      }
      setDraggingSource(null);
      setDraggingSelectedIndex(null);
      setSelectedDropIndexSafely(null);
      setAlternativeDropActiveSafely(false);
  };

  const handleAlternativeDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setAlternativeDropActiveSafely(true);
      setSelectedDropIndexSafely(null);
  };

  const handleAlternativeDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
      const relatedTarget = e.relatedTarget as Node | null;
      if (relatedTarget && e.currentTarget.contains(relatedTarget)) {
          return;
      }
      setAlternativeDropActiveSafely(false);
  };

  const handleAlternativeDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const dragPayload = getDragPayload(e);
      if (!dragPayload || dragPayload.source !== 'selected' || dragPayload.index === null) return;

      moveSelectedTrickToAlternative(dragPayload.index);
      setDraggingSource(null);
      setDraggingSelectedIndex(null);
      setSelectedDropIndexSafely(null);
      setAlternativeDropActiveSafely(false);
  };

  const currentViewingSourceList = useMemo(() =>
    visibleMasterLists.find(l => l.id === viewingSourceListId) || visibleMasterLists[0],
  [viewingSourceListId, visibleMasterLists]);

  const sourceTrickGroups = useMemo(() => {
      const otherPrelimTricks = new Set<string>();
      if (roundCategory === 'PRELIM1') {
          config.prelim2Configs.forEach(c => {
              c.selectedTricks.forEach(sel => otherPrelimTricks.add(`${sel.masterListId}-${sel.trickId}`));
              if (c.alternativeTrick) otherPrelimTricks.add(`${c.alternativeTrick.masterListId}-${c.alternativeTrick.trickId}`);
          });
      } else if (roundCategory === 'PRELIM2') {
          config.prelim1Configs.forEach(c => {
              c.selectedTricks.forEach(sel => otherPrelimTricks.add(`${sel.masterListId}-${sel.trickId}`));
              if (c.alternativeTrick) otherPrelimTricks.add(`${c.alternativeTrick.masterListId}-${c.alternativeTrick.trickId}`);
          });
      }

      const groups: Record<number, TournamentTrick[]> = {};
      currentViewingSourceList.tricks
          .filter(t => !activeRoundConfig.selectedTricks.some(sel => sel.trickId === t.id && sel.masterListId === viewingSourceListId))
          .filter(t => activeRoundConfig.alternativeTrick?.trickId !== t.id || activeRoundConfig.alternativeTrick?.masterListId !== viewingSourceListId)
          .filter(t => !otherPrelimTricks.has(`${viewingSourceListId}-${t.id}`))
          .filter(t => roundCategory === 'FINAL' ? true : (t.score >= PRELIM_MIN_TRICK_SCORE && t.score <= PRELIM_MAX_TRICK_SCORE))
          .forEach(t => {
              if (!groups[t.score]) groups[t.score] = [];
              groups[t.score].push(t);
          });

      return Object.keys(groups)
          .map(Number)
          .sort((a, b) => a - b)
          .map(score => ({ score, tricks: groups[score] }));
  }, [
      activeRoundConfig.alternativeTrick,
      activeRoundConfig.selectedTricks,
      config.prelim1Configs,
      config.prelim2Configs,
      currentViewingSourceList,
      roundCategory,
      viewingSourceListId
  ]);

  const handleAlternativeGuideClick = () => {
      sourceSelectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

      if (!sourceTrickGroups.some(group => expandedScores[group.score])) {
          const firstScore = sourceTrickGroups[0]?.score;
          if (typeof firstScore === 'number') {
              setExpandedScores({ [firstScore]: true });
          }
      }

      setIsPrimaryGuideActive(false);
      setIsAlternativeGuideActive(true);
  };

  const handlePrimaryGuideClick = () => {
      sourceSelectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

      if (!sourceTrickGroups.some(group => expandedScores[group.score])) {
          const firstScore = sourceTrickGroups[0]?.score;
          if (typeof firstScore === 'number') {
              setExpandedScores({ [firstScore]: true });
          }
      }

      setIsAlternativeGuideActive(false);
      setIsPrimaryGuideActive(true);
  };

  const selectedTrickScoreSummary = useMemo(() => {
    const summary = new Map<number, number>();
    let totalScore = 0;
    let squaredScore = 0;
    let bonusScore = 0;

    activeRoundConfig.selectedTricks.forEach(reference => {
      const trick = getReferencedTrick(reference, config.masterLists);
      if (!trick) return;
      totalScore += trick.score;
      squaredScore += Math.pow(trick.score, 2);
      bonusScore += getFinalBonusForScore(trick.score);
      summary.set(trick.score, (summary.get(trick.score) || 0) + 1);
    });

    const breakdown = Array.from(summary.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([score, count]) => ({
        score,
        count,
        subtotal: score * count,
        squaredSubtotal: Math.pow(score, 2) * count,
        bonusSubtotal: count * getFinalBonusForScore(score)
      }));

    return {
      totalScore,
      squaredScore,
      bonusScore,
      allSuccessBonusScore: totalScore,
      breakdown
    };
  }, [activeRoundConfig.selectedTricks, config.masterLists]);

  return (
    <div className="w-full max-w-5xl mx-auto p-2 md:p-4 flex flex-col h-full text-white pt-10 overflow-y-auto custom-scrollbar relative">
      <div className="flex items-center justify-between mb-4 shrink-0">
        <button onClick={onBack} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
          <ArrowLeft size={20} /> <span className="hidden sm:inline font-orbitron uppercase tracking-widest text-xs">Back</span>
        </button>
        <h2 className="text-xl md:text-2xl font-orbitron font-bold neon-glow-blue uppercase tracking-widest">Tournament Setup</h2>
        <div className="w-8"></div>
      </div>

      <div className="flex gap-4 border-b border-zinc-800 mb-4 shrink-0">
          <button onClick={() => setActiveTab('SETUP')} className={`pb-2 px-4 font-orbitron font-bold text-sm tracking-widest transition-colors ${activeTab === 'SETUP' ? 'text-yellow-400 border-b-2 border-yellow-400' : 'text-zinc-500 hover:text-zinc-300'}`}>ROUND SETUP / 轮次</button>
          <button onClick={() => setActiveTab('MASTER')} className={`pb-2 px-4 font-orbitron font-bold text-sm tracking-widest transition-colors ${activeTab === 'MASTER' ? 'text-yellow-400 border-b-2 border-yellow-400' : 'text-zinc-500 hover:text-zinc-300'}`}>MANAGE LISTS / 总表</button>
      </div>

      <div className="flex-1">
          {activeTab === 'MASTER' ? (
              <div className="flex flex-col gap-4">
                  <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                      <select value={selectedMasterListId} onChange={e => setSelectedMasterListId(e.target.value)} className="flex-grow bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-white font-rajdhani outline-none focus:border-yellow-500">
                          {visibleMasterLists.map(l => <option key={l.id} value={l.id}>{l.name} {l.isPreset ? '(Preset)' : ''}</option>)}
                      </select>
                  </div>

                  {isCreatingList && (
                      <form onSubmit={handleCreateList} className="flex gap-2 p-3 bg-zinc-900 rounded-lg shrink-0 border border-cyan-900/50 animate-in slide-in-from-top-2">
                          <input autoFocus type="text" value={newListName} onChange={e => setNewListName(e.target.value)} placeholder="New List Name..." className="flex-grow bg-black border border-zinc-700 rounded px-3 py-2 text-white outline-none focus:border-cyan-500" />
                          <button type="submit" className="bg-cyan-600 hover:bg-cyan-500 text-white px-4 rounded font-bold">CREATE</button>
                      </form>
                  )}

                  {!currentMasterList.isPreset ? (
                      <div className="flex flex-col gap-3 shrink-0">
                          <form onSubmit={handleAddTrick} className="flex gap-2 p-3 bg-zinc-900 rounded-lg border border-zinc-800">
                              <input type="text" value={newTrickName} onChange={e => setNewTrickName(e.target.value)} placeholder="Trick Name / 招式名称" className="flex-grow bg-black border border-zinc-700 rounded px-3 py-2 text-white outline-none font-rajdhani" />
                              <input type="number" value={newTrickScore} onChange={e => setNewTrickScore(e.target.value)} placeholder="Pts" className="w-20 bg-black border border-zinc-700 rounded px-3 py-2 text-white outline-none font-orbitron text-center" />
                              <button type="submit" className="bg-yellow-600 hover:bg-yellow-500 text-black font-bold px-4 rounded"><Plus size={20} /></button>
                          </form>
                          
                          <div className="flex items-center">
                              <input type="file" accept=".xlsx, .xls, .csv" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />
                              <button onClick={() => fileInputRef.current?.click()} className="flex items-center justify-center gap-2 w-full bg-green-900/30 text-green-400 border border-green-500/50 p-2 rounded-lg hover:bg-green-900/50 transition-colors text-sm">
                                  <FileSpreadsheet size={16} /> Import Tricks from Excel / 从 Excel 导入招式
                              </button>
                          </div>
                      </div>
                  ) : (
                      <div className="p-3 bg-blue-900/10 border border-blue-500/30 rounded-lg text-blue-400 text-xs font-rajdhani flex items-center gap-3"><ShieldAlert size={16} /> Preset list - Read Only. / 预设表 - 只读。</div>
                  )}

                  <div className="flex-1 overflow-y-auto custom-scrollbar space-y-6 pr-2">
                      {groupedTricks.map(({ score, tricks }) => (
                          <div key={score} className="space-y-2">
                              <div className="flex items-center gap-2 px-1">
                                  <div className="bg-yellow-600/80 text-black font-orbitron font-bold px-2 py-0.5 rounded text-[10px]">{score} PTS</div>
                                  <div className="flex-grow h-px bg-zinc-800"></div>
                              </div>
                              <div className="grid grid-cols-1 gap-2">
                                  {tricks.map(trick => (
                                      <div key={trick.id} className="flex justify-between items-center bg-zinc-800/20 p-3 rounded border border-zinc-800/50">
                                          {editingTrickId === trick.id ? (
                                              <div className="flex w-full gap-2 items-center">
                                                  <input type="text" value={editName} onChange={e => setEditName(e.target.value)} className="flex-grow bg-black border border-zinc-600 rounded px-2 py-1 text-sm" />
                                                  <input type="number" value={editScore} onChange={e => setEditScore(e.target.value)} className="w-16 bg-black border border-zinc-600 rounded px-2 py-1 text-sm text-center" />
                                                  <button onClick={saveEditing} className="text-green-500"><Check size={18} /></button>
                                                  <button onClick={() => setEditingTrickId(null)} className="text-zinc-500"><X size={18} /></button>
                                              </div>
                                          ) : (
                                              <>
                                                  <span className="font-rajdhani text-lg whitespace-pre-wrap">{trick.name}</span>
                                                  {!currentMasterList.isPreset && (
                                                      <div className="flex gap-2">
                                                          <button onClick={() => { setEditingTrickId(trick.id); setEditName(trick.name); setEditScore(trick.score.toString()); }} className="text-blue-500 p-1 hover:text-blue-300 transition-colors"><Edit2 size={16} /></button>
                                                          <button onClick={() => handleDeleteTrick(trick.id)} className="text-zinc-600 p-1 hover:text-red-500 transition-colors"><Trash2 size={16} /></button>
                                                      </div>
                                                  )}
                                              </>
                                          )}
                                      </div>
                                  ))}
                              </div>
                          </div>
                      ))}
                  </div>
              </div>
          ) : (
              <div className="flex flex-col gap-4">
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3 flex flex-col gap-4 items-center shrink-0">
                      <div className="flex bg-black rounded-lg p-1 w-full overflow-x-auto no-scrollbar">
                          <button onClick={() => setRoundCategory('PRELIM1')} className={`flex-1 min-w-[100px] px-3 py-1.5 rounded-md text-[10px] md:text-xs font-orbitron transition-all ${roundCategory === 'PRELIM1' ? 'bg-yellow-600 text-black font-bold' : 'text-zinc-500 hover:text-zinc-300'}`}>PRELIM 1 / 初赛 1</button>
                          <button onClick={() => setRoundCategory('PRELIM2')} className={`flex-1 min-w-[100px] px-3 py-1.5 rounded-md text-[10px] md:text-xs font-orbitron transition-all ${roundCategory === 'PRELIM2' ? 'bg-yellow-600 text-black font-bold' : 'text-zinc-500 hover:text-zinc-300'}`}>PRELIM 2 / 初赛 2</button>
                          <button onClick={() => setRoundCategory('FINAL')} className={`flex-1 min-w-[100px] px-3 py-1.5 rounded-md text-[10px] md:text-xs font-orbitron transition-all ${roundCategory === 'FINAL' ? 'bg-yellow-600 text-black font-bold' : 'text-zinc-500 hover:text-zinc-300'}`}>FINALS / 决赛</button>
                      </div>

                      <div className="flex items-center gap-2 w-full">
                          <div className="relative flex-grow">
                             <select value={selectedConfigId} onChange={e => setSelectedConfigId(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white font-rajdhani outline-none appearance-none">
                                {currentConfigs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                             </select>
                             <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-zinc-500"><ChevronDown size={14} /></div>
                          </div>
                          
                          <button onClick={() => { setEditingConfigName(activeRoundConfig.name); setIsEditingConfigName(true); }} className="text-blue-500 p-2 hover:bg-blue-900/20 rounded-lg"><Edit2 size={16} /></button>
                          <button onClick={() => setIsCreatingConfig(!isCreatingConfig)} className="text-green-500 p-2 hover:bg-green-900/20 rounded-lg"><FolderPlus size={18} /></button>
                          <button onClick={() => handleDeleteRoundConfig(activeRoundConfig.id)} className="text-red-500 p-2 hover:bg-red-900/20 rounded-lg"><Trash2 size={18} /></button>
                      </div>
                  </div>

                  {isEditingConfigName && (
                      <div className="flex gap-2 p-2 bg-blue-900/20 border border-blue-500/30 rounded-lg shrink-0 animate-in slide-in-from-top-1">
                          <input autoFocus type="text" value={editingConfigName} onChange={e => setEditingConfigName(e.target.value)} className="flex-grow bg-black border border-zinc-700 rounded px-3 py-1 text-sm outline-none" />
                          <button onClick={handleRenameConfig} className="bg-blue-600 text-white px-3 rounded text-xs font-bold">SAVE</button>
                          <button onClick={() => setIsEditingConfigName(false)} className="text-zinc-400"><X size={18} /></button>
                      </div>
                  )}

                  {isCreatingConfig && (
                      <form onSubmit={handleCreateRoundConfig} className="flex gap-2 p-2 bg-green-900/20 border border-green-500/30 rounded-lg shrink-0 animate-in slide-in-from-top-1">
                          <input autoFocus type="text" value={newConfigName} onChange={e => setNewConfigName(e.target.value)} placeholder="New Profile Name..." className="flex-grow bg-black border border-zinc-700 rounded px-3 py-1 text-sm outline-none" />
                          <button type="submit" className="bg-green-600 text-white px-3 rounded text-xs font-bold">CREATE</button>
                          <button onClick={() => setIsCreatingConfig(false)} className="text-zinc-400"><X size={18} /></button>
                      </form>
                  )}

                  <div className="flex-1 flex flex-col md:flex-row gap-4">
                      <div className="flex-1 flex flex-col bg-zinc-900/50 rounded-xl border border-zinc-800 p-3">
                          <div className="flex justify-between text-[10px] text-zinc-500 mb-2 font-orbitron uppercase tracking-widest px-1">
                              <span>Selected Tricks / 已选招式</span>
                              <span>{activeRoundConfig.selectedTricks.length} / {roundCategory === 'FINAL' ? 30 : 10}</span>
                          </div>

                          <div
                              ref={selectedListRef}
                              className="space-y-2 pr-1"
                              onDragOver={handleListDragOver}
                              onDrop={handleListDrop}
                          >
                              {draggingSource !== null ? (
                                  <div className="px-1 text-[10px] font-orbitron uppercase tracking-[0.14em] text-cyan-300/80">
                                      {draggingSource === 'alternative'
                                          ? 'Drop to insert into regular list / 拖回正式招式列表'
                                          : 'Drag to reorder / 拖动调整顺序'}
                                  </div>
                              ) : null}
                              {activeRoundConfig.selectedTricks.length === 0 && (
                                  <button
                                      type="button"
                                      onClick={handlePrimaryGuideClick}
                                      className={`w-full text-center p-4 border border-dashed rounded-lg transition-all ${
                                          isPrimaryGuideActive
                                              ? 'border-green-400/60 bg-green-500/10 text-green-200'
                                              : 'border-zinc-800 text-zinc-700 hover:border-green-500/30 hover:bg-green-500/5 hover:text-green-300'
                                      }`}
                                  >
                                      <div className="text-[10px] font-rajdhani italic">None selected / 未设置</div>
                                      <div className="mt-2 text-[10px] font-orbitron uppercase tracking-[0.14em]">
                                          Click to choose regular tricks / 点击去选择正式招式
                                      </div>
                                  </button>
                              )}

                              {activeRoundConfig.selectedTricks.map((sel, idx) => {
                                  const list = config.masterLists.find(l => l.id === sel.masterListId);
                                  const trick = list?.tricks.find(t => t.id === sel.trickId);
                                  if (!trick) return null;
                                  const isDraggedItem = draggingSource === 'selected' && draggingSelectedIndex === idx;
                                      const isLongPressReady = selectedLongPressReadyIndex === idx;
                                  const showDropIndicatorAbove = draggingSource !== null && selectedDropIndex === idx;
                                  return (
                                      <div
                                          key={`${sel.masterListId}-${sel.trickId}-${idx}`}
                                          className="relative"
                                          data-selected-index={idx}
                                      >
                                        {showDropIndicatorAbove ? (
                                            <div className="pointer-events-none absolute -top-2 left-2 right-2 z-10 flex items-center gap-1">
                                                <span className="h-1.5 w-1.5 rounded-full bg-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.7)] animate-pulse" />
                                                <div className="h-0.5 flex-1 rounded-full bg-cyan-400 shadow-[0_0_14px_rgba(34,211,238,0.6)] animate-pulse" />
                                                <span className="h-1.5 w-1.5 rounded-full bg-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.7)] animate-pulse" />
                                            </div>
                                        ) : null}
                                        <div
                                          className={`relative flex flex-col rounded-xl border p-2 group cursor-grab active:cursor-grabbing select-none transform-gpu will-change-transform transition-[transform,box-shadow,border-color,background-color,opacity] duration-200 ease-out ${
                                              isDraggedItem
                                                  ? 'border-cyan-300/70 bg-cyan-500/12 shadow-[0_18px_40px_rgba(34,211,238,0.24)] scale-[1.03] -rotate-[0.6deg] opacity-20'
                                                  : isLongPressReady
                                                  ? 'border-cyan-300/80 bg-cyan-500/12 shadow-[0_0_0_1px_rgba(103,232,249,0.28),0_0_28px_rgba(34,211,238,0.26)] scale-[1.02]'
                                                  : 'border-zinc-800/50 bg-black/40 hover:-translate-y-0.5 hover:border-cyan-500/30 hover:bg-cyan-500/[0.04] hover:shadow-[0_10px_24px_rgba(8,145,178,0.12)]'
                                          }`}
                                          onPointerDown={(e) => handleSelectedPointerDown(e, idx)}
                                          onPointerMove={handleSelectedPointerMove}
                                          onPointerUp={handleSelectedPointerUp}
                                          onPointerCancel={handleSelectedPointerCancel}
                                          onTouchStart={(e) => handleSelectedTouchStart(e, idx)}
                                          onTouchMove={handleSelectedTouchMove}
                                          onTouchEnd={handleSelectedTouchEnd}
                                          onTouchCancel={handleSelectedTouchCancel}
                                        >
                                              {isLongPressReady ? (
                                                  <div className="pointer-events-none absolute right-2 top-2 rounded-md border border-cyan-300/50 bg-cyan-400/15 px-1.5 py-1 text-[9px] font-orbitron uppercase tracking-[0.16em] text-cyan-100 shadow-[0_0_18px_rgba(34,211,238,0.22)] animate-pulse">
                                                      Ready
                                                  </div>
                                              ) : null}
                                              <div className="flex justify-between items-center mb-1 pointer-events-none">
                                                  <div className="flex items-center gap-2 overflow-hidden">
                                                      <div
                                                          aria-hidden="true"
                                                        className={`grid grid-cols-2 gap-x-1 gap-y-[3px] rounded-sm px-1 py-1 transition-[color,transform,opacity] duration-200 ${
                                                              isDraggedItem ? 'text-cyan-200/90 scale-110' : 'text-zinc-600/80 group-hover:scale-105 group-hover:text-cyan-300/80'
                                                          }`}
                                                      >
                                                          {DRAG_HANDLE_DOTS.map((dotIndex) => (
                                                              <span key={dotIndex} className="h-[2px] w-[2px] rounded-full bg-current" />
                                                          ))}
                                                      </div>
                                                      <span className="text-zinc-600 font-mono text-[10px] w-4">{idx + 1}</span>
                                                      <span className="font-rajdhani text-sm whitespace-pre-wrap">{trick.name}</span>
                                                  </div>
                                                  <div className="flex items-center gap-2 shrink-0 pointer-events-auto">
                                                        <span className="text-yellow-500/60 font-orbitron text-[10px]">{trick.score}</span>
                                                        {(roundCategory === 'PRELIM1' || roundCategory === 'PRELIM2') && (
                                                            <button 
                                                                type="button"
                                                                onClick={() => requestMoveTrickToOtherPrelim(idx, false)} 
                                                                className="text-blue-400 hover:text-blue-300 transition-colors" 
                                                                title={`Move to ${roundCategory === 'PRELIM1' ? 'PRELIM 2 / 初赛 2' : 'PRELIM 1 / 初赛 1'}`}
                                                            >
                                                                <ArrowRightLeft size={14} />
                                                            </button>
                                                        )}
                                                        <button onClick={() => handleRemoveRoundTrick(idx)} className="text-red-400 hover:text-red-300 transition-colors"><Trash2 size={14} /></button>
                                                    </div>
                                              </div>
                                          <div className="text-[8px] text-zinc-600 font-rajdhani uppercase tracking-tighter self-end italic pointer-events-none">
                                              Source: {list?.name}
                                          </div>
                                        </div>
                                      </div>
                                  );
                              })}

                              {draggingSource !== null && selectedDropIndex === activeRoundConfig.selectedTricks.length ? (
                                  <div className="rounded-xl border border-dashed border-cyan-400/50 bg-cyan-500/8 px-3 py-2 text-center text-[10px] font-orbitron uppercase tracking-[0.14em] text-cyan-200/90 shadow-[0_0_18px_rgba(34,211,238,0.16)] animate-pulse">
                                      {draggingSource === 'alternative'
                                          ? 'Drop here to append to regular list / 松开放到正式招式末尾'
                                          : 'Drop here to move to bottom / 松开放到末尾'}
                                  </div>
                              ) : null}

                              {/* Alternative Trick Section */}
                              {(roundCategory === 'PRELIM1' || roundCategory === 'PRELIM2') && (
                                <div
                                    data-alternative-drop-zone="true"
                                    className={`mt-4 pt-4 border-t transition-colors ${
                                        isAlternativeDropActive ? 'border-cyan-400/50' : 'border-zinc-800'
                                    }`}
                                    onDragOver={handleAlternativeDragOver}
                                    onDragLeave={handleAlternativeDragLeave}
                                    onDrop={handleAlternativeDrop}
                                >
                                    <div className="text-[10px] text-purple-500 font-orbitron uppercase tracking-widest mb-2 px-1 flex items-center gap-2">
                                        <Replace size={12} /> Alternative Trick / 备选招式
                                    </div>
                                    {alternativeDropNotice ? (
                                        <div className="mb-2 rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-[10px] leading-relaxed text-amber-200">
                                            {alternativeDropNotice}
                                        </div>
                                    ) : null}
                                    {draggingSource === 'selected' ? (
                                        <div className={`mb-2 rounded-xl border px-3 py-2 text-center text-[10px] font-orbitron uppercase tracking-[0.14em] transform-gpu transition-[transform,box-shadow,border-color,background-color,color] duration-200 ease-out ${
                                            isAlternativeDropActive
                                                ? 'border-cyan-300/60 bg-cyan-500/12 text-cyan-100 shadow-[0_16px_36px_rgba(34,211,238,0.18)] scale-[1.01]'
                                                : 'border-purple-500/20 bg-purple-500/5 text-purple-200/80'
                                        }`}>
                                            {activeRoundConfig.alternativeTrick
                                                ? 'Drop here to swap with alternative / 松开放到这里可与备选招交换'
                                                : 'Drop here to set alternative / 松开放到这里设为备选招'}
                                        </div>
                                    ) : null}
                                    {activeRoundConfig.alternativeTrick ? (
                                        (() => {
                                            const sel = activeRoundConfig.alternativeTrick;
                                            const list = config.masterLists.find(l => l.id === sel.masterListId);
                                            const trick = list?.tricks.find(t => t.id === sel.trickId);
                                            const isAlternativeDragged = draggingSource === 'alternative';
                                            return (
                                                <div className={`flex flex-col rounded-xl border p-2 group cursor-grab active:cursor-grabbing select-none transform-gpu will-change-transform transition-[transform,box-shadow,border-color,background-color,opacity] duration-200 ease-out ${
                                                    isAlternativeDragged
                                                        ? 'bg-cyan-500/12 border-cyan-300/70 shadow-[0_18px_40px_rgba(34,211,238,0.24)] scale-[1.03] -rotate-[0.6deg] opacity-20'
                                                        : isAlternativeDropActive
                                                        ? 'bg-cyan-500/10 border-cyan-300/60 shadow-[0_12px_30px_rgba(34,211,238,0.18)]'
                                                        : 'bg-purple-900/20 border-purple-500/30 hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(168,85,247,0.14)]'
                                                }`}
                                                onPointerDown={handleAlternativePointerDown}
                                                onPointerMove={handleAlternativePointerMove}
                                                onPointerUp={handleAlternativePointerUp}
                                                onPointerCancel={handleAlternativePointerCancel}
                                                onTouchStart={handleAlternativeTouchStart}
                                                onTouchMove={handleAlternativeTouchMove}
                                                onTouchEnd={handleAlternativeTouchEnd}
                                                onTouchCancel={handleAlternativeTouchCancel}
                                            >
                                                    <div className="flex justify-between items-center pointer-events-none">
                                                        <div className="flex items-center gap-2 overflow-hidden">
                                                            <div
                                                                aria-hidden="true"
                                                                className={`grid grid-cols-2 gap-x-1 gap-y-[3px] rounded-sm px-1 py-1 transition-[color,transform,opacity] duration-200 ${
                                                                    isAlternativeDragged ? 'text-cyan-200/90 scale-110' : 'text-purple-300/70 group-hover:scale-105 group-hover:text-cyan-300/80'
                                                                }`}
                                                            >
                                                                {DRAG_HANDLE_DOTS.map((dotIndex) => (
                                                                    <span key={dotIndex} className="h-[2px] w-[2px] rounded-full bg-current" />
                                                                ))}
                                                            </div>
                                                            <span className="font-rajdhani text-sm truncate text-purple-100">{trick?.name || 'Unknown'}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 pointer-events-auto">
                                                            <span className="text-purple-400 font-orbitron text-[10px]">{trick?.score}</span>
                                                            {(roundCategory === 'PRELIM1' || roundCategory === 'PRELIM2') && (
                                                                <button 
                                                                    type="button"
                                                                    onClick={() => requestMoveTrickToOtherPrelim(0, true)} 
                                                                    className="text-blue-400 hover:text-blue-300 transition-colors" 
                                                                    title={`Move to ${roundCategory === 'PRELIM1' ? 'PRELIM 2 / 初赛 2' : 'PRELIM 1 / 初赛 1'}`}
                                                                >
                                                                    <ArrowRightLeft size={14} />
                                                                </button>
                                                            )}
                                                            <button onClick={() => handleRemoveRoundTrick(0, true)} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleAlternativeGuideClick}
                                            className={`w-full text-center p-4 border border-dashed rounded-lg transition-all ${
                                                isAlternativeDropActive
                                                    ? 'border-cyan-400/50 bg-cyan-500/10 text-cyan-200'
                                                    : isAlternativeGuideActive
                                                    ? 'border-purple-400/60 bg-purple-500/10 text-purple-200'
                                                    : 'border-zinc-800 text-zinc-700 hover:border-purple-500/30 hover:bg-purple-500/5 hover:text-purple-300'
                                            }`}
                                        >
                                            <div className="text-[10px] font-rajdhani italic">None selected / 未设置</div>
                                            <div className="mt-2 text-[10px] font-orbitron uppercase tracking-[0.14em]">
                                                Click to choose an alternative trick / 点击去选择备选招
                                            </div>
                                        </button>
                                    )}
                                </div>
                              )}
                          </div>

                          <div className="mt-4 pt-3 border-t border-zinc-800 shrink-0">
                              <div className="flex justify-between items-center mb-2">
                                    <div className="text-[10px] text-zinc-500 font-orbitron uppercase tracking-widest flex items-center gap-2">
                                        <Bookmark size={12} /> {roundCategory === 'FINAL' ? 'Final Score Preview / 决赛得分构成' : 'Total Base Score / 基础总分'}
                                    </div>
                                    <div className="text-xl font-rajdhani font-black text-yellow-500 neon-glow-blue">
                                        {roundCategory === 'FINAL' ? selectedTrickScoreSummary.squaredScore + selectedTrickScoreSummary.bonusScore + selectedTrickScoreSummary.allSuccessBonusScore + parseInt(previewInitialScore || '0', 10) : selectedTrickScoreSummary.totalScore} PTS
                                    </div>
                                </div>
                                {roundCategory === 'FINAL' && selectedTrickScoreSummary.breakdown.length > 0 && (
                                    <div className="mb-3 space-y-2">
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                            <div className="rounded-md border border-green-500/20 bg-black/30 px-3 py-2 text-[10px] font-orbitron tracking-wide text-zinc-300 flex flex-col justify-between">
                                                <div className="text-zinc-500 mb-1">初始分 / Initial</div>
                                                <div className="flex items-center mt-1">
                                                    <input 
                                                        type="number" 
                                                        value={previewInitialScore} 
                                                        onChange={e => setPreviewInitialScore(e.target.value)} 
                                                        className="w-full bg-black/50 border border-zinc-700 rounded text-green-400 text-sm px-1 py-0.5 outline-none focus:border-green-500"
                                                        min="0"
                                                    />
                                                </div>
                                            </div>
                                            <div className="rounded-md border border-cyan-500/20 bg-black/30 px-3 py-2 text-[10px] font-orbitron tracking-wide text-zinc-300 flex flex-col justify-between">
                                                <div className="text-zinc-500 mb-1">乘算分 / Squared</div>
                                                <div className="text-cyan-400 text-sm mt-1">{selectedTrickScoreSummary.squaredScore} PTS</div>
                                            </div>
                                            <div className="rounded-md border border-amber-500/20 bg-black/30 px-3 py-2 text-[10px] font-orbitron tracking-wide text-zinc-300 flex flex-col justify-between">
                                                <div className="text-zinc-500 mb-1">奖励分 / Bonus</div>
                                                <div className="text-amber-400 text-sm mt-1">{selectedTrickScoreSummary.bonusScore} PTS</div>
                                            </div>
                                            <div className="rounded-md border border-fuchsia-500/20 bg-black/30 px-3 py-2 text-[10px] font-orbitron tracking-wide text-zinc-300 flex flex-col justify-between">
                                                <div className="text-zinc-500 mb-1">全成追加 / Full Mark</div>
                                                <div className="text-fuchsia-400 text-sm mt-1">+{selectedTrickScoreSummary.allSuccessBonusScore} PTS</div>
                                            </div>
                                        </div>
                                        <div className="rounded-md border border-zinc-700 bg-black/20 px-3 py-2 text-[10px] font-rajdhani text-zinc-400">
                                            最终得分 = 初始分({parseInt(previewInitialScore || '0', 10)}) + 乘算分({selectedTrickScoreSummary.squaredScore}) + 奖励分({selectedTrickScoreSummary.bonusScore})；若全成，额外再加 {selectedTrickScoreSummary.allSuccessBonusScore} 分。
                                        </div>
                                      <div className="flex flex-wrap gap-2">
                                          {selectedTrickScoreSummary.breakdown.map(({ score, count, squaredSubtotal, bonusSubtotal }) => (
                                              <div
                                                key={score}
                                                className="rounded-md border border-yellow-500/20 bg-black/30 px-2 py-1 text-[10px] font-orbitron tracking-wide text-zinc-300"
                                              >
                                                  {score}PTS x {count} = {squaredSubtotal}{bonusSubtotal > 0 ? ` (+${bonusSubtotal})` : ''}
                                              </div>
                                          ))}
                                      </div>
                                  </div>
                              )}
                          </div>
                      </div>

                      <div
                          ref={sourceSelectionRef}
                          className={`flex-1 flex flex-col rounded-xl border p-3 transition-all ${
                              isPrimaryGuideActive
                                  ? 'bg-green-950/20 border-green-500/40 shadow-[0_0_0_1px_rgba(34,197,94,0.25)]'
                                  : isAlternativeGuideActive
                                  ? 'bg-purple-950/20 border-purple-500/40 shadow-[0_0_0_1px_rgba(168,85,247,0.25)]'
                                  : 'bg-zinc-900/50 border-zinc-800'
                          }`}
                      >
                          <div className="mb-3 shrink-0">
                              <label className="text-[10px] text-zinc-500 font-orbitron uppercase tracking-widest block mb-1 flex items-center gap-2">
                                  <ListFilter size={12} /> Source List / 选择总表
                              </label>
                              <select value={viewingSourceListId} onChange={e => handleViewingSourceListChange(e.target.value)} className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-1.5 text-xs text-yellow-500 font-rajdhani outline-none focus:border-yellow-500">
                                  {visibleMasterLists.map(l => <option key={l.id} value={l.id}>{l.name} {l.isPreset ? '(Preset)' : ''}</option>)}
                              </select>
                              {isPrimaryGuideActive && (
                                  <div className="mt-2 text-[10px] text-green-300 font-orbitron uppercase tracking-[0.14em]">
                                      Use the green plus button to add regular tricks / 点击绿色加号按钮添加正式招式
                                  </div>
                              )}
                              {isAlternativeGuideActive && (
                                  <div className="mt-2 text-[10px] text-purple-300 font-orbitron uppercase tracking-[0.14em]">
                                      Use the purple button to set your alternative trick / 点击紫色按钮设置备选招
                                  </div>
                              )}
                          </div>

                          <div className="space-y-1 pr-1">
                              {sourceTrickGroups.map(({ score, tricks }) => {
                                  const isExpanded = expandedScores[score] ?? false;
                                  return (
                                      <div key={score} className="border border-zinc-800/50 rounded-lg overflow-hidden mb-1">
                                          <button onClick={() => setExpandedScores(prev => prev[score] ? {} : { [score]: true })} className="w-full flex items-center justify-between p-2 bg-black/20 hover:bg-zinc-800/40">
                                              <span className="font-orbitron text-[10px] text-zinc-400">{score} PTS</span>
                                              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                          </button>
                                          {isExpanded && (
                                              <div className="p-1 space-y-1 bg-black/10">
                                                  {tricks.map(trick => (
                                                      <div key={trick.id} className="flex justify-between items-center bg-zinc-900/40 p-2 rounded hover:border-zinc-700 border border-transparent transition-colors group">
                                                          <span className="font-rajdhani text-xs whitespace-pre-wrap mr-2">{trick.name}</span>
                                                          <div className="flex gap-1">
                                                              {(roundCategory === 'PRELIM1' || roundCategory === 'PRELIM2') && (
                                                                  <button
                                                                      onClick={() => handleAddRoundTrick(trick.id, true)}
                                                                      className={`p-1 rounded border transition-all ${
                                                                          isAlternativeGuideActive
                                                                              ? 'text-purple-200 bg-purple-500/20 border-purple-400/60 animate-pulse shadow-[0_0_12px_rgba(168,85,247,0.35)]'
                                                                              : 'text-purple-400 hover:bg-purple-900/20 border-purple-500/20'
                                                                      }`}
                                                                      title="Set as Alternative"
                                                                  >
                                                                      <Replace size={14}/>
                                                                  </button>
                                                              )}
                                                              <button
                                                                  onClick={() => handleAddRoundTrick(trick.id)}
                                                                  className={`p-1 rounded transition-all ${
                                                                      isPrimaryGuideActive
                                                                          ? 'text-green-200 bg-green-500/20 border border-green-400/60 animate-pulse shadow-[0_0_12px_rgba(34,197,94,0.35)]'
                                                                          : 'text-green-500 hover:bg-green-900/20'
                                                                  }`}
                                                              >
                                                                  <Plus size={16}/>
                                                              </button>
                                                          </div>
                                                      </div>
                                                  ))}
                                              </div>
                                          )}
                                      </div>
                                  );
                              })}
                          </div>
                      </div>
                  </div>
              </div>
          )}

          {selectedDragPreview && draggingSelectedIndex !== null ? (
              (() => {
                  const previewSelection = activeRoundConfig.selectedTricks[draggingSelectedIndex];
                  const previewList = previewSelection
                      ? config.masterLists.find(l => l.id === previewSelection.masterListId)
                      : undefined;
                  const previewTrick = previewSelection
                      ? previewList?.tricks.find(t => t.id === previewSelection.trickId)
                      : undefined;
                  return previewTrick ? (
                      <div
                          className="pointer-events-none fixed z-[140] transform-gpu"
                          style={{
                              left: selectedDragPreview.x,
                              top: selectedDragPreview.y,
                              width: selectedDragPreview.width,
                          }}
                      >
                          <div className="rounded-xl border border-cyan-300/70 bg-zinc-950/92 p-2 shadow-[0_22px_48px_rgba(34,211,238,0.28)] backdrop-blur-sm transition-transform duration-150 ease-out scale-[1.03] -rotate-[1.2deg]">
                              <div className="mb-1 flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2 overflow-hidden">
                                      <div
                                          aria-hidden="true"
                                          className="grid grid-cols-2 gap-x-1 gap-y-[3px] rounded-sm px-1 py-1 text-cyan-200/90"
                                      >
                                          {DRAG_HANDLE_DOTS.map((dotIndex) => (
                                              <span key={dotIndex} className="h-[2px] w-[2px] rounded-full bg-current" />
                                          ))}
                                      </div>
                                      <span className="w-4 text-[10px] font-mono text-cyan-200/70">{draggingSelectedIndex + 1}</span>
                                      <span className="whitespace-pre-wrap font-rajdhani text-sm text-cyan-50">
                                          {previewTrick.name}
                                      </span>
                                  </div>
                                  <span className="font-orbitron text-[10px] text-cyan-200/80">{previewTrick.score}</span>
                              </div>
                              <div className="text-right text-[8px] font-rajdhani uppercase tracking-tighter italic text-cyan-200/55">
                                  Source: {previewList?.name}
                              </div>
                          </div>
                      </div>
                  ) : null;
              })()
          ) : null}

          {alternativeDragPreview && activeRoundConfig.alternativeTrick ? (
              (() => {
                  const previewList = config.masterLists.find(l => l.id === activeRoundConfig.alternativeTrick?.masterListId);
                  const previewTrick = previewList?.tricks.find(t => t.id === activeRoundConfig.alternativeTrick?.trickId);
                  return (
                      <div
                          className="pointer-events-none fixed z-[140] transform-gpu"
                          style={{
                              left: alternativeDragPreview.x,
                              top: alternativeDragPreview.y,
                              width: alternativeDragPreview.width,
                          }}
                      >
                          <div className="rounded-xl border border-cyan-300/70 bg-zinc-950/92 p-2 shadow-[0_22px_48px_rgba(34,211,238,0.28)] backdrop-blur-sm transition-transform duration-150 ease-out scale-[1.03] -rotate-[1.2deg]">
                              <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2 overflow-hidden">
                                      <div
                                          aria-hidden="true"
                                          className="grid grid-cols-2 gap-x-1 gap-y-[3px] rounded-sm px-1 py-1 text-cyan-200/90"
                                      >
                                          {DRAG_HANDLE_DOTS.map((dotIndex) => (
                                              <span key={dotIndex} className="h-[2px] w-[2px] rounded-full bg-current" />
                                          ))}
                                      </div>
                                      <span className="truncate font-rajdhani text-sm text-cyan-50">
                                          {previewTrick?.name || 'Unknown'}
                                      </span>
                                  </div>
                                  <span className="font-orbitron text-[10px] text-cyan-200/80">{previewTrick?.score}</span>
                              </div>
                          </div>
                      </div>
                  );
              })()
          ) : null}
      </div>

      {pendingMoveConfirmation && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-md rounded-2xl border border-cyan-500/30 bg-zinc-950 p-5 shadow-2xl">
            <div className="flex items-center gap-2 text-cyan-300 font-orbitron text-sm uppercase tracking-[0.18em]">
              <ArrowRightLeft size={16} />
              Confirm Move / 确认移动
            </div>
            <p className="mt-4 text-sm leading-relaxed text-zinc-200">
              {pendingMoveConfirmation.message}
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPendingMoveConfirmation(null)}
                className="rounded-xl border border-white/15 px-4 py-2 text-sm text-zinc-300 transition-colors hover:border-white/30 hover:text-white"
              >
                Cancel / 取消
              </button>
              <button
                type="button"
                onClick={() => {
                  handleMoveTrickToOtherPrelim(
                    pendingMoveConfirmation.idx,
                    pendingMoveConfirmation.isAlternative
                  );
                  setPendingMoveConfirmation(null);
                }}
                className="rounded-xl border border-cyan-400/35 bg-cyan-500/15 px-4 py-2 text-sm text-cyan-200 transition-colors hover:bg-cyan-500/25"
              >
                Confirm / 确认
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 快速开始：跳过返回菜单的步骤，直接进入初赛 1 / 初赛 2 / 决赛的轮次选择 */}
      {onQuickStart && (
        <button
          type="button"
          onClick={onQuickStart}
          aria-label="Quick Start / 快速开始"
          className="fixed bottom-[calc(1rem_+_var(--safe-bottom))] right-4 z-[110] flex items-center gap-2 rounded-full border border-yellow-400/50 bg-yellow-500/20 px-5 py-3 font-orbitron text-xs font-bold uppercase tracking-widest text-yellow-100 shadow-[0_0_22px_rgba(234,179,8,0.32)] backdrop-blur-md transition-all hover:border-yellow-300/70 hover:bg-yellow-500/30 hover:text-white active:scale-95 md:bottom-6 md:right-6"
        >
          <Zap size={16} />
          <span>Quick Start / 快速开始</span>
        </button>
      )}
    </div>
  );
};

export default TournamentEditor;
